// src/pages/MobiliarioPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  Armchair,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  Tag,
  Eye,
  FileSpreadsheet,
  LoaderPinwheel,
  LayoutGrid,
  List,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Archive,
  MapPin,
} from "lucide-react";
import Navbar from "../components/ui/Navbar";
import SelectField from "../components/ui/SelectField";
import PageSizeSelect from "../components/ui/PageSizeSelect";
import Pagination from "../components/ui/Pagination";
import StatCard from "../components/ui/StatCard";
import MobiliarioForm from "../components/ui/MobiliarioForm";
import ModalImportarExcel from "../components/modals/ModalImportarExcel";
import { hasPermission } from "../utils/permissions";
import { descargarArchivo } from "../utils/descargas";
import { ESTADO_BADGE, TIPOS_MOBILIARIO, toOptions } from "../constants/mobiliario";

const VISTA_KEY = "mobiliario.vista";

const leerVista = () => {
  try {
    return localStorage.getItem(VISTA_KEY) || "tabla";
  } catch {
    return "tabla";
  }
};

const EstadoBadge = ({ estado }) =>
  estado ? (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
        ESTADO_BADGE[estado] || "bg-gray-100 text-gray-600 border border-gray-200"
      }`}
    >
      {estado}
    </span>
  ) : (
    "—"
  );

const marcaModelo = (it) =>
  [it.marca?.name, it.modelo?.name].filter(Boolean).join(" / ") || "—";

const MobiliarioPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filtros, setFiltros] = useState({ cliente: "", sede: "", area: "", tipo: "" });
  const [filtroEstado, setFiltroEstado] = useState("");

  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tipos, setTipos] = useState(TIPOS_MOBILIARIO);

  const [vista, setVista] = useState(leerVista);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [descargando, setDescargando] = useState(null);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const puede = {
    crear: hasPermission("mobiliario.crear"),
    editar: hasPermission("mobiliario.editar"),
    eliminar: hasPermission("mobiliario.eliminar"),
    importar: hasPermission("mobiliario.importar"),
  };

  const fetchItems = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const params = { search, ...filtros, ...overrides };
        Object.keys(params).forEach((k) => !params[k] && delete params[k]);
        const res = await axios.get(`${API}/mobiliario`, { headers, params });
        setItems(res.data || []);
        setPage(1);
      } catch (err) {
        toast.error(err.response?.data?.message || "Error al cargar el mobiliario");
      } finally {
        setLoading(false);
      }
    },
    [API, headers, search, filtros],
  );

  const fetchCatalogos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mobiliario/catalogos`, { headers });
      if (res.data?.tipos?.length) setTipos(res.data.tipos);
    } catch {
      /* se usan los tipos por defecto */
    }
  }, [API, headers]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/clientes`, { headers }),
      axios.get(`${API}/areas`, { headers }),
    ])
      .then(([c, a]) => {
        setClientes(c.data || []);
        setAreas(a.data || []);
      })
      .catch(() => toast.error("No se pudieron cargar los filtros"));
    fetchCatalogos();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarFiltro = async (key, value) => {
    const next = { ...filtros, [key]: value };
    if (key === "cliente") {
      next.sede = "";
      setSedes([]);
      if (value) {
        try {
          setSedes((await axios.get(`${API}/sedes/${value}`, { headers })).data || []);
        } catch {
          setSedes([]);
        }
      }
    }
    setFiltros(next);
    fetchItems(next);
  };

  const limpiarFiltros = () => {
    const vacio = { cliente: "", sede: "", area: "", tipo: "" };
    setSearch("");
    setFiltros(vacio);
    setFiltroEstado("");
    setSedes([]);
    fetchItems({ ...vacio, search: "" });
  };

  const cambiarVista = (v) => {
    setVista(v);
    try {
      localStorage.setItem(VISTA_KEY, v);
    } catch {
      /* preferencia no persistida */
    }
  };

  /* ---------- Acciones ---------- */
  const handleSave = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/mobiliario/${editing._id}`, payload, { headers });
        toast.success("Elemento actualizado");
      } else {
        await axios.post(`${API}/mobiliario`, payload, { headers });
        toast.success("Elemento creado");
      }
      setShowForm(false);
      setEditing(null);
      fetchItems();
      fetchCatalogos();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar el elemento");
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.tipo}" con código ${it.codigo}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/mobiliario/${it._id}`, { headers });
      toast.info("Elemento eliminado");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al eliminar el elemento");
    }
  };

  const handleEtiqueta = async (it) => {
    setDescargando(it._id);
    try {
      await descargarArchivo(`/mobiliario/${it._id}/etiqueta`, `etiqueta_${it.codigo}.pdf`);
    } catch {
      toast.error("Error al generar la etiqueta");
    } finally {
      setDescargando(null);
    }
  };

  /* ---------- Derivados ---------- */
  const conteo = useMemo(() => {
    const c = { Bueno: 0, Regular: 0, Malo: 0, "Dado de baja": 0 };
    items.forEach((it) => {
      if (c[it.estado] !== undefined) c[it.estado]++;
    });
    return c;
  }, [items]);

  const visibles = useMemo(
    () => (filtroEstado ? items.filter((it) => it.estado === filtroEstado) : items),
    [items, filtroEstado],
  );

  const paged = useMemo(
    () => visibles.slice((page - 1) * pageSize, page * pageSize),
    [visibles, page, pageSize],
  );

  const toggleEstado = (estado) => {
    setFiltroEstado((prev) => (prev === estado ? "" : estado));
    setPage(1);
  };

  const hayFiltros = search || filtroEstado || Object.values(filtros).some(Boolean);

  const Acciones = ({ it, compact = false }) => (
    <div className={`flex items-center gap-1.5 ${compact ? "" : "justify-end"}`}>
      <Link
        to={`/mobiliario/${it._id}`}
        title="Ver ficha"
        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
      >
        <Eye className="h-3.5 w-3.5" />
      </Link>
      <button
        onClick={() => handleEtiqueta(it)}
        disabled={descargando === it._id}
        title="Imprimir etiqueta QR"
        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
      >
        {descargando === it._id ? (
          <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Tag className="h-3.5 w-3.5" />
        )}
      </button>
      {puede.editar && (
        <button
          onClick={() => {
            setEditing(it);
            setShowForm(true);
          }}
          title="Editar"
          className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      {puede.eliminar && (
        <button
          onClick={() => handleDelete(it)}
          title="Eliminar"
          className="p-1.5 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  const Vacio = () => (
    <div className="py-16 text-center">
      <Armchair className="h-10 w-10 text-gray-200 mx-auto mb-3" />
      <p className="text-gray-500 font-medium">No se encontraron elementos</p>
      <p className="text-gray-400 text-xs mt-1">
        {hayFiltros ? "Intenta ajustar los filtros" : "Registra un elemento o impórtalos desde Excel"}
      </p>
    </div>
  );

  const Tarjetas = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
      {paged.map((it) => (
        <div
          key={it._id}
          className="rounded-xl border border-gray-200 bg-white p-4 hover:shadow-md hover:border-blue-200 transition-all flex flex-col gap-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <span className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                <Armchair className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <Link
                  to={`/mobiliario/${it._id}`}
                  className="block font-semibold text-gray-900 hover:text-blue-600 truncate"
                >
                  {it.tipo}
                </Link>
                <span className="font-mono text-xs text-blue-600">{it.codigo}</span>
              </div>
            </div>
            <EstadoBadge estado={it.estado} />
          </div>
          {it.descripcion && <p className="text-xs text-gray-500 line-clamp-2">{it.descripcion}</p>}
          <div className="text-xs text-gray-500 space-y-1">
            <p className="flex items-center gap-1.5 truncate">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              {[it.sede?.name, it.area?.name].filter(Boolean).join(" · ") || "—"}
            </p>
            <p className="truncate">
              <span className="text-gray-400">Marca/Modelo:</span> {marcaModelo(it)}
            </p>
            <p className="truncate">
              <span className="text-gray-400">Inventario:</span> {it.inventario || "—"}
            </p>
          </div>
          <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400 truncate">{it.cliente?.name}</span>
            <Acciones it={it} compact />
          </div>
        </div>
      ))}
    </div>
  );

  const Tabla = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
            <th className="py-3 px-4 text-left">Código</th>
            <th className="py-3 px-4 text-left">Tipo</th>
            <th className="py-3 px-4 text-left">Cliente</th>
            <th className="py-3 px-4 text-left">Sede</th>
            <th className="py-3 px-4 text-left">Área</th>
            <th className="py-3 px-4 text-left">Marca / Modelo</th>
            <th className="py-3 px-4 text-left">Inventario</th>
            <th className="py-3 px-4 text-left">Estado</th>
            <th className="py-3 px-4 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {paged.map((it) => (
            <tr key={it._id} className="hover:bg-gray-50 transition-colors">
              <td className="py-2.5 px-4">
                <Link
                  to={`/mobiliario/${it._id}`}
                  className="text-blue-600 hover:underline font-mono text-xs font-semibold"
                >
                  {it.codigo}
                </Link>
              </td>
              <td className="py-2.5 px-4">
                <span className="font-medium text-gray-800">{it.tipo}</span>
                {it.descripcion && (
                  <span className="block text-xs text-gray-400 truncate max-w-[200px]">{it.descripcion}</span>
                )}
              </td>
              <td className="py-2.5 px-4 text-gray-700">{it.cliente?.name || "—"}</td>
              <td className="py-2.5 px-4 text-gray-600">{it.sede?.name || "—"}</td>
              <td className="py-2.5 px-4 text-gray-600">{it.area?.name || "—"}</td>
              <td className="py-2.5 px-4 text-gray-700">{marcaModelo(it)}</td>
              <td className="py-2.5 px-4 font-mono text-xs text-gray-600">{it.inventario || "—"}</td>
              <td className="py-2.5 px-4">
                <EstadoBadge estado={it.estado} />
              </td>
              <td className="py-2.5 px-4">
                <Acciones it={it} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Armchair className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mobiliario</h1>
              <p className="text-sm text-gray-500">
                Inventario de elementos sin mantenimiento · {items.length} registrado
                {items.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {puede.importar && (
              <button
                onClick={() => setShowImport(true)}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                Importar Excel
              </button>
            )}
            {puede.crear && (
              <button
                onClick={() => {
                  setEditing(null);
                  setShowForm(true);
                }}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                Nuevo elemento
              </button>
            )}
          </div>
        </div>

        {/* KPIs (clic = filtrar por estado) */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <StatCard
            icon={Armchair}
            label="Total"
            value={items.length}
            tone="blue"
            active={!filtroEstado}
            onClick={() => toggleEstado("")}
          />
          <StatCard icon={CheckCircle2} label="Buen estado" value={conteo.Bueno} tone="green" active={filtroEstado === "Bueno"} onClick={() => toggleEstado("Bueno")} />
          <StatCard icon={AlertTriangle} label="Regular" value={conteo.Regular} tone="amber" active={filtroEstado === "Regular"} onClick={() => toggleEstado("Regular")} />
          <StatCard icon={XCircle} label="Mal estado" value={conteo.Malo} tone="red" active={filtroEstado === "Malo"} onClick={() => toggleEstado("Malo")} />
          <StatCard icon={Archive} label="Dados de baja" value={conteo["Dado de baja"]} tone="gray" active={filtroEstado === "Dado de baja"} onClick={() => toggleEstado("Dado de baja")} />
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchItems()}
                  placeholder="Código, serial, inventario… (Enter)"
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <SelectField
              label="Cliente"
              name="cliente"
              value={filtros.cliente}
              options={clientes}
              placeholder="Todos los clientes"
              onChange={(e) => cambiarFiltro("cliente", e.target.value)}
            />
            <SelectField
              label="Sede"
              name="sede"
              value={filtros.sede}
              options={sedes}
              placeholder={filtros.cliente ? "Todas las sedes" : "Elige un cliente"}
              isDisabled={!filtros.cliente}
              onChange={(e) => cambiarFiltro("sede", e.target.value)}
            />
            <SelectField
              label="Área"
              name="area"
              value={filtros.area}
              options={areas}
              placeholder="Todas las áreas"
              onChange={(e) => cambiarFiltro("area", e.target.value)}
            />
            <SelectField
              label="Tipo"
              name="tipo"
              value={filtros.tipo}
              options={toOptions(tipos)}
              placeholder="Todos los tipos"
              onChange={(e) => cambiarFiltro("tipo", e.target.value)}
            />
          </div>

          {hayFiltros && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full font-medium">
                Filtros activos · {visibles.length} resultado{visibles.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={limpiarFiltros}
                className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Limpiar
              </button>
            </div>
          )}
        </div>

        {/* Listado */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100">
            <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {[
                { key: "tabla", icon: List, label: "Tabla" },
                { key: "tarjetas", icon: LayoutGrid, label: "Tarjetas" },
              ].map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  onClick={() => cambiarVista(key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    vista === key ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            <PageSizeSelect
              value={pageSize}
              onChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
              className="ml-auto"
            />
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <LoaderPinwheel className="h-6 w-6 animate-spin text-blue-400 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">Cargando mobiliario...</p>
            </div>
          ) : paged.length === 0 ? (
            <Vacio />
          ) : (
            <>
              {/* En móvil siempre tarjetas; en escritorio según preferencia */}
              <div className="md:hidden">
                <Tarjetas />
              </div>
              <div className="hidden md:block">{vista === "tarjetas" ? <Tarjetas /> : <Tabla />}</div>
            </>
          )}
        </div>

        <Pagination page={page} pageSize={pageSize} total={visibles.length} onPageChange={setPage} />
      </div>

      {showForm && (
        <MobiliarioForm
          item={editing}
          tipos={tipos}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      {showImport && (
        <ModalImportarExcel
          titulo="Importar mobiliario"
          descripcion="Carga masiva de sillas, mesas, escritorios y otros elementos"
          endpoint="/mobiliario/importar"
          plantilla={{ path: "/mobiliario/plantilla", filename: "Formato_Importacion_Mobiliario.xlsx" }}
          onClose={() => setShowImport(false)}
          onImported={() => {
            fetchItems();
            fetchCatalogos();
          }}
        />
      )}
    </div>
  );
};

export default MobiliarioPage;
